import React, { useEffect, useId, useRef, useState } from 'react';
import ReactCountryFlag from 'react-country-flag';

const LANGUAGE_OPTIONS = [
  { code: 'fr', name: 'Français', countryCode: 'FR' },
  { code: 'en', name: 'English', countryCode: 'GB' },
  { code: 'de', name: 'Deutsch', countryCode: 'DE' },
  { code: 'pt', name: 'Português', countryCode: 'PT' },
  { code: 'es', name: 'Español', countryCode: 'ES' },
  { code: 'sl', name: 'Slovenščina', countryCode: 'SI' },
  { code: 'uk', name: 'Українська', countryCode: 'UA' },
  { code: 'lt', name: 'Lietuvių', countryCode: 'LT' },
  { code: 'sv', name: 'Svenska', countryCode: 'SE' }
];

const LanguageSelector = ({ language, setLanguage }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const containerRef = useRef(null);
  const buttonRef = useRef(null);
  const optionRefs = useRef([]);
  const baseId = useId();
  const listboxId = `${baseId}-listbox`;
  const buttonId = `${baseId}-button`;

  const activeLanguage = LANGUAGE_OPTIONS.find((option) => option.code === language) || LANGUAGE_OPTIONS[0];
  const activeIndex = LANGUAGE_OPTIONS.findIndex((option) => option.code === activeLanguage.code);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!containerRef.current) {
        return;
      }

      if (!containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const handleFocusOutside = (event) => {
      if (!containerRef.current) {
        return;
      }

      if (!containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('focusin', handleFocusOutside);

    return () => {
      document.removeEventListener('focusin', handleFocusOutside);
    };
  }, []);

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
        buttonRef.current?.focus();
      }
    };

    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  useEffect(() => {
    if (isOpen) {
      const targetIndex = activeIndex >= 0 ? activeIndex : 0;
      setFocusedIndex(targetIndex);
    } else {
      setFocusedIndex(-1);
    }
  }, [isOpen, activeIndex]);

  useEffect(() => {
    if (isOpen && focusedIndex >= 0) {
      optionRefs.current[focusedIndex]?.focus();
    }
  }, [focusedIndex, isOpen]);

  const moveFocus = (direction) => {
    if (!isOpen) {
      return;
    }
    setFocusedIndex((prev) => {
      const nextIndex = (prev + direction + LANGUAGE_OPTIONS.length) % LANGUAGE_OPTIONS.length;
      return nextIndex;
    });
  };

  const handleSelectLanguage = (code) => {
    if (code !== language) {
      setLanguage(code);
    }
    setIsOpen(false);
    buttonRef.current?.focus();
  };

  const handleButtonKeyDown = (event) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (!isOpen) {
        setIsOpen(true);
      } else {
        moveFocus(event.key === 'ArrowDown' ? 1 : -1);
      }
    }
  };

  const handleOptionKeyDown = (event, index) => {
    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowRight':
        event.preventDefault();
        moveFocus(1);
        break;
      case 'ArrowUp':
      case 'ArrowLeft':
        event.preventDefault();
        moveFocus(-1);
        break;
      case 'Home':
        event.preventDefault();
        setFocusedIndex(0);
        break;
      case 'End':
        event.preventDefault();
        setFocusedIndex(LANGUAGE_OPTIONS.length - 1);
        break;
      case 'Enter':
      case ' ': // Space
        event.preventDefault();
        handleSelectLanguage(LANGUAGE_OPTIONS[index].code);
        break;
      case 'Escape':
        event.preventDefault();
        setIsOpen(false);
        buttonRef.current?.focus();
        break;
      default:
        break;
    }
  };

  optionRefs.current = [];
  return (
    <div className="language-selector-wrapper" ref={containerRef}>
      <button
        id={buttonId}
        ref={buttonRef}
        type="button"
        className={`language-selector ${isOpen ? 'language-selector--open' : ''}`}
        onClick={() => setIsOpen((prev) => !prev)}
        onKeyDown={handleButtonKeyDown}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-controls={listboxId}
      >
        <ReactCountryFlag
          svg
          countryCode={activeLanguage.countryCode}
          className="language-selector__flag"
          aria-label={activeLanguage.name}
          style={{ width: '1.25em', height: '1.25em', borderRadius: '50%' }}
        />
        <span className="language-selector__text">{activeLanguage.name}</span>
        <span aria-hidden="true" className="language-selector__chevron" />
      </button>

      {isOpen && (
        <ul
          className="language-selector__menu"
          role="listbox"
          id={listboxId}
          aria-labelledby={buttonId}
          aria-activedescendant={
            focusedIndex >= 0 ? `${listboxId}-${LANGUAGE_OPTIONS[focusedIndex].code}` : undefined
          }
          tabIndex={0}
        >
          {LANGUAGE_OPTIONS.map((option, index) => (
            <li key={option.code}>
              <button
                id={`${listboxId}-${option.code}`}
                ref={(node) => {
                  optionRefs.current[index] = node;
                }}
                type="button"
                role="option"
                onClick={() => handleSelectLanguage(option.code)}
                onKeyDown={(event) => handleOptionKeyDown(event, index)}
                aria-selected={option.code === language}
                className={`language-selector__option ${option.code === language ? 'is-active' : ''}`}
                tabIndex={focusedIndex === index ? 0 : -1}
              >
                <ReactCountryFlag
                  svg
                  countryCode={option.countryCode}
                  className="language-selector__flag"
                  aria-label={option.name}
                  style={{ width: '1.25em', height: '1.25em', borderRadius: '50%' }}
                />
                <span className="language-selector__text">{option.name}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default LanguageSelector;
